import { degToRad } from '../domain/engine.js';
import type { ConstraintDefinition, ScenarioDocument } from '../domain/model.js';

const CREATED = '2026-08-04T12:00:00.000Z';

const STANDARD_CONSTRAINTS: readonly ConstraintDefinition[] = [
  { id: 'overshoot', metric: 'overshoot_percent', comparator: '<=', threshold: 10, required: true, unit: '%', rationale: 'Peak overshoot must remain within the recorded illustrative limit.' },
  { id: 'settling', metric: 'settling_time_s', comparator: '<=', threshold: 9, required: true, unit: 's', rationale: 'Settling time must remain within the recorded finite window.' },
  { id: 'final-error', metric: 'final_abs_error_rad', comparator: '<=', threshold: degToRad(0.2), required: true, unit: 'rad', rationale: 'Absolute final error must remain within 0.2°.' },
  { id: 'saturation', metric: 'saturation_fraction', comparator: '<=', threshold: 0.25, required: true, unit: '1', rationale: 'Actuator saturation exposure must remain within the recorded illustrative fraction.' },
];

const BASE_SETTINGS = {
  displayAngleUnit: 'deg' as const,
  stepMetricSettings: {
    riseLowerFraction: 0.1,
    riseUpperFraction: 0.9,
    settlingBand: { kind: 'fraction_of_step' as const, value: 0.02 },
    settlingHoldDuration: 1,
  },
  refinementCriteria: {
    angle: { selected: true, tolerance: degToRad(0.02) },
    angularRate: { selected: true, tolerance: degToRad(0.05) },
    appliedTorque: { selected: true, tolerance: 0.03 },
  },
  constraints: STANDARD_CONSTRAINTS,
  tuning: {
    grid: {
      kp: [16, 20, 24],
      ki: [0, 1, 2],
      kd: [20, 24, 28],
      derivativeFilterTimeConstant: [0, 0.05],
      trackingTimeConstant: [0.25, 0.4],
    },
    constraints: STANDARD_CONSTRAINTS,
    primaryObjective: 'iae_rad_s' as const,
    requireLinearStability: true,
    skipAnalyticallyUnstable: true,
    maximumCandidates: 5000,
  },
};

export const SPACECRAFT_DEMO: ScenarioDocument = {
  schemaVersion: 1,
  id: 'demo-spacecraft-attitude-step',
  name: 'Spacecraft attitude step and disturbance rejection',
  intendedQuestion: 'How does a bounded single-axis PID controller track a 10° command while rejecting a short disturbance torque?',
  parameterProvenance: 'Synthetic portfolio demonstration values selected to show saturation, anti-windup and event-aligned disturbance rejection. They do not represent a real spacecraft.',
  assumptions: 'One rigid principal axis. Cross-axis coupling, quaternions, wheel momentum storage, flexible modes, actuator dynamics and orbital-frame effects are omitted.',
  notionalDemo: true,
  demoId: 'spacecraft_attitude_step',
  createdAt: CREATED,
  updatedAt: CREATED,
  ...BASE_SETTINGS,
  scenario: {
    mode: 'spacecraft_single_axis',
    plant: { inertia: 12, damping: 0, stiffness: 0 },
    controller: {
      kp: 20,
      ki: 2,
      kd: 28,
      derivativeFilterTimeConstant: 0.05,
      antiWindup: { enabled: true, trackingTimeConstant: 0.4 },
    },
    actuator: { maximumTorque: 2 },
    reference: { kind: 'step', time: 0.37, before: 0, after: degToRad(10) },
    disturbance: { kind: 'rectangular_pulse', startTime: 5.15, endTime: 5.65, amplitude: 1 },
    noise: { enabled: false, seed: 17, angleStandardDeviation: 0, rateStandardDeviation: 0 },
    initialState: { angle: 0, angularRate: 0, integralTorque: 0, filteredMeasuredRate: 0 },
    simulation: { startTime: 0, endTime: 15, nominalStep: 0.01, abortAbsoluteStateBound: 1000 },
  },
};

export const AIRCRAFT_DEMO: ScenarioDocument = {
  schemaVersion: 1,
  id: 'demo-aircraft-pitch-surrogate',
  name: 'Pitch-axis surrogate gust-equivalent disturbance rejection',
  intendedQuestion: 'How does a notional pitch-axis surrogate track a 3° command and reject a finite gust-equivalent pitch torque?',
  parameterProvenance: 'Synthetic equivalent coefficients for software and controls demonstration only. No aerodynamic derivative set or flight condition is represented.',
  assumptions: 'Equivalent single-axis rotational model around one operating condition. Angle of attack, flight-path angle, speed, altitude, Mach number, actuator dynamics and lateral coupling are omitted.',
  notionalDemo: true,
  demoId: 'aircraft_pitch_surrogate',
  createdAt: CREATED,
  updatedAt: CREATED,
  ...BASE_SETTINGS,
  constraints: [
    { ...STANDARD_CONSTRAINTS[0]!, threshold: 20 },
    { ...STANDARD_CONSTRAINTS[1]!, threshold: 10 },
    { ...STANDARD_CONSTRAINTS[2]!, threshold: degToRad(0.25), rationale: 'Absolute final error must remain within 0.25°.' },
    { ...STANDARD_CONSTRAINTS[3]!, threshold: 0.2 },
  ],
  tuning: {
    grid: {
      kp: [7000, 9000, 12000],
      ki: [1200, 1800, 2500],
      kd: [3000, 4500, 6000],
      derivativeFilterTimeConstant: [0.04, 0.08],
      trackingTimeConstant: [0.25, 0.4],
    },
    constraints: [
      { ...STANDARD_CONSTRAINTS[0]!, threshold: 20 },
      { ...STANDARD_CONSTRAINTS[1]!, threshold: 10 },
      { ...STANDARD_CONSTRAINTS[2]!, threshold: degToRad(0.25) },
      { ...STANDARD_CONSTRAINTS[3]!, threshold: 0.2 },
    ],
    primaryObjective: 'itae_rad_s2',
    requireLinearStability: true,
    skipAnalyticallyUnstable: true,
    maximumCandidates: 5000,
  },
  scenario: {
    mode: 'aircraft_pitch_surrogate',
    plant: { inertia: 2200, damping: 900, stiffness: 1800 },
    controller: {
      kp: 9000,
      ki: 2500,
      kd: 4500,
      derivativeFilterTimeConstant: 0.04,
      antiWindup: { enabled: true, trackingTimeConstant: 0.4 },
    },
    actuator: { maximumTorque: 4500 },
    reference: { kind: 'step', time: 0.55, before: 0, after: degToRad(3) },
    disturbance: { kind: 'rectangular_pulse', startTime: 3.1, endTime: 3.85, amplitude: 100 },
    noise: { enabled: false, seed: 31, angleStandardDeviation: 0, rateStandardDeviation: 0 },
    initialState: { angle: 0, angularRate: 0, integralTorque: 0, filteredMeasuredRate: 0 },
    simulation: { startTime: 0, endTime: 15, nominalStep: 0.01, abortAbsoluteStateBound: 1000 },
  },
};

export const COARSE_STEP_DEMO: ScenarioDocument = {
  ...structuredClone(SPACECRAFT_DEMO),
  id: 'demo-coarse-time-step',
  name: 'Deliberately coarse time-step demonstration',
  intendedQuestion: 'Can a smooth-looking control response still fail explicit Δt versus Δt/2 numerical criteria?',
  parameterProvenance: 'Synthetic demonstration using an intentionally coarse 0.1 s nominal step. The illustrative refinement limits are 0.001° angle, 0.001°/s rate and 0.002 N·m applied torque so the example exposes a failed numerical check. Replace them with study-specific requirements for engineering use.',
  demoId: 'coarse_time_step_demo',
  scenario: {
    ...structuredClone(SPACECRAFT_DEMO.scenario),
    controller: {
      ...structuredClone(SPACECRAFT_DEMO.scenario.controller),
      kp: 24,
      ki: 3,
      kd: 8,
      derivativeFilterTimeConstant: 0.04,
    },
    simulation: { startTime: 0, endTime: 8, nominalStep: 0.1, abortAbsoluteStateBound: 1000 },
  },
  refinementCriteria: {
    angle: { selected: true, tolerance: degToRad(0.001) },
    angularRate: { selected: true, tolerance: degToRad(0.001) },
    appliedTorque: { selected: true, tolerance: 0.002 },
  },
};

export const NO_FEASIBLE_DEMO: ScenarioDocument = {
  ...structuredClone(SPACECRAFT_DEMO),
  id: 'demo-no-feasible-tuning',
  name: 'No feasible PID search demonstration',
  intendedQuestion: 'How does a finite deterministic search report that no candidate meets every required constraint?',
  demoId: 'no_feasible_tuning',
  constraints: [
    { ...STANDARD_CONSTRAINTS[0]!, threshold: 0 },
    { ...STANDARD_CONSTRAINTS[1]!, threshold: 0.2 },
    { ...STANDARD_CONSTRAINTS[2]!, threshold: 0.00001 },
    { ...STANDARD_CONSTRAINTS[3]!, threshold: 0 },
  ],
  tuning: {
    grid: {
      kp: [16, 20, 24],
      ki: [0, 1, 2],
      kd: [20, 24, 28],
      derivativeFilterTimeConstant: [0.05],
      trackingTimeConstant: [0.4],
    },
    constraints: [
      { ...STANDARD_CONSTRAINTS[0]!, threshold: 0 },
      { ...STANDARD_CONSTRAINTS[1]!, threshold: 0.2 },
      { ...STANDARD_CONSTRAINTS[2]!, threshold: 0.00001 },
      { ...STANDARD_CONSTRAINTS[3]!, threshold: 0 },
    ],
    primaryObjective: 'iae_rad_s',
    requireLinearStability: true,
    skipAnalyticallyUnstable: true,
    maximumCandidates: 5000,
  },
};

export const DEMOS: readonly ScenarioDocument[] = [SPACECRAFT_DEMO, AIRCRAFT_DEMO, COARSE_STEP_DEMO, NO_FEASIBLE_DEMO];

export function cloneScenario(document: ScenarioDocument): ScenarioDocument {
  return structuredClone(document);
}

export function blankScenario(): ScenarioDocument {
  const now = new Date().toISOString();
  const base = cloneScenario(SPACECRAFT_DEMO);
  return {
    ...base,
    id: `scenario-${crypto.randomUUID()}`,
    name: 'Untitled single-axis control study',
    intendedQuestion: 'Record the engineering question this scenario is intended to answer.',
    parameterProvenance: '',
    assumptions: base.assumptions,
    notionalDemo: false,
    demoId: null,
    createdAt: now,
    updatedAt: now,
  };
}
